import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Search, ArrowRight, BookOpen, Clock, Tag, CalendarDays } from "lucide-react";
import Seo, { SITE_URL } from "../Seo"; // WebsiteComponents/blogs/ -> WebsiteComponents/Seo
import { ARTICLES, formatDate } from "./articles";

const PATH = "/blogs";
const TITLE = "CRM & Lead Management Blog: Guides & Tips | LMSBOTS";
const DESCRIPTION =
  "Practical CRM and lead management guides from LMSBOTS: pipelines, WhatsApp automation, click-to-call, and CRM for education, healthcare and real estate.";

const JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "LMSBOTS Blog",
    description: DESCRIPTION,
    url: `${SITE_URL}${PATH}`,
    inLanguage: "en-IN",
    publisher: { "@id": `${SITE_URL}/#organization` },
    blogPost: ARTICLES.map((a) => ({
      "@type": "BlogPosting",
      headline: a.title,
      url: `${SITE_URL}/blogs/${a.id}`,
      datePublished: a.published,
      description: a.metaDescription,
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: ARTICLES.map((a, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE_URL}/blogs/${a.id}`,
      name: a.title,
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Blogs", item: `${SITE_URL}${PATH}` },
    ],
  },
];

const CATEGORIES = ["All", ...Array.from(new Set(ARTICLES.map((a) => a.category)))];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

const Blogs = () => {
  const reduced = useReducedMotion();
  const item = reduced ? undefined : fadeUp;
  const group = reduced ? undefined : stagger;
  const initial = reduced ? false : "hidden";

  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredPosts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return ARTICLES.filter((post) => {
      const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
      const matchesSearch =
        !q ||
        post.title.toLowerCase().includes(q) ||
        post.summary.toLowerCase().includes(q) ||
        post.keywords.some((k) => k.toLowerCase().includes(q));
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  return (
    <>
      <Seo title={TITLE} description={DESCRIPTION} path={PATH} jsonLd={JSON_LD} />

      <div className="w-full overflow-x-clip bg-white font-sans text-[#28194f] antialiased">
        {/* ===== HERO SECTION ===== */}
        <section
          id="blogs-hero"
          className="relative isolate w-full overflow-hidden bg-gradient-to-br from-[#fbf7ff] via-white to-[#effbf8] pb-16 pt-28 sm:pb-20 sm:pt-36 lg:pb-24 lg:pt-40"
        >
          <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-[#702cb0]/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-[#12dfbd]/15 blur-3xl" />

          <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <motion.div initial={initial} animate="visible" variants={group} className="mx-auto flex flex-col items-center">
              <motion.div
                variants={item}
                className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#48247f]/15 bg-white px-3.5 py-1 text-xs font-semibold text-[#48247f] shadow-sm"
              >
                <BookOpen className="h-3.5 w-3.5 text-[#702cb0]" />
                LMSBOTS Knowledge Hub
              </motion.div>

              <motion.h1
                variants={item}
                className="text-balance text-[32px] font-light leading-[1.2] tracking-tight text-[#28194f] sm:text-[42px] lg:text-[48px]"
              >
                CRM &amp; lead management{" "}
                <span className="bg-gradient-to-r from-[#48247f] to-[#702cb0] bg-clip-text font-normal text-transparent">
                  guides that help you grow
                </span>
              </motion.h1>

              <motion.p
                variants={item}
                className="mx-auto mt-4 max-w-2xl text-sm font-light leading-[1.75] text-[#3b2a63] sm:text-base lg:text-[17px]"
              >
                In-depth articles on CRM software, lead management systems, sales pipelines, WhatsApp automation and
                click-to-call, with playbooks for education, healthcare and real estate teams.
              </motion.p>

              <motion.div variants={item} className="mt-8 w-full max-w-xl">
                <div className="relative flex items-center">
                  <Search className="absolute left-4 h-4 w-4 text-slate-400" />
                  <input
                    type="search"
                    aria-label="Search articles"
                    placeholder="Search CRM, lead management, WhatsApp..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-2xl border border-[#48247f]/15 bg-white py-3.5 pl-11 pr-4 text-sm text-[#28194f] shadow-sm outline-none transition-all focus:border-[#702cb0] focus:ring-2 focus:ring-[#702cb0]/20"
                  />
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ===== BLOG LIST SECTION ===== */}
        <section id="blogs-grid-section" className="relative w-full bg-white py-14 sm:py-16 lg:py-24">
          <div className="mx-auto w-full max-w-[1300px] px-4 sm:px-10 lg:px-14">
            <div className="mb-12 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`rounded-full px-4 py-2 text-xs font-semibold transition-all sm:text-sm ${
                    selectedCategory === cat
                      ? "bg-[#48247f] text-white shadow-md shadow-[#48247f]/20"
                      : "border border-[#48247f]/15 bg-white text-[#48247f] hover:bg-[#f1ecfa]"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {filteredPosts.length === 0 ? (
              <div className="py-20 text-center">
                <p className="text-base text-slate-500">No articles found matching your search criteria.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                {filteredPosts.map((post) => (
                  <article
                    key={post.id}
                    className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-[#48247f]/10 bg-white p-6 shadow-[0_8px_30px_-12px_rgba(72,36,127,0.25)] transition-all duration-300 hover:-translate-y-1 hover:border-[#702cb0]/40 hover:shadow-[0_24px_48px_-12px_rgba(72,36,127,0.4)] motion-reduce:transition-none sm:p-8"
                  >
                    <div className="absolute left-0 top-0 h-full w-1 origin-top scale-y-0 bg-gradient-to-b from-[#12dfbd] via-[#702cb0] to-[#48247f] transition-transform duration-500 ease-out group-hover:scale-y-100" />

                    <div>
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f1ecfa] px-3 py-1 text-xs font-semibold text-[#48247f]">
                          <Tag className="h-3.5 w-3.5 text-[#702cb0]" />
                          {post.category}
                        </span>
                        <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-slate-500">
                          <Clock className="h-3.5 w-3.5" />
                          {post.readTime}
                        </span>
                      </div>

                      <h2 className="mb-3 text-lg font-medium leading-snug tracking-tight text-[#28194f] sm:text-xl">
                        <Link to={`/blogs/${post.id}`} className="transition-colors hover:text-[#48247f]">
                          {post.title}
                        </Link>
                      </h2>
                      <p className="text-sm font-normal leading-relaxed text-slate-700">{post.summary}</p>
                    </div>

                    <div className="mt-6 flex items-center justify-between border-t border-[#48247f]/10 pt-4">
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                        <CalendarDays className="h-3.5 w-3.5" />
                        <time dateTime={post.published}>{formatDate(post.published)}</time>
                      </span>
                      <Link
                        to={`/blogs/${post.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#702cb0] transition-colors hover:text-[#48247f]"
                      >
                        Read article{" "}
                        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Blogs;









